/**
 * De materiaalstaat van één kar: per onderdeel de maten, het materiaal, de afwerking en
 * daarachter **een kolom per bewerking** met wat daar moet gebeuren.
 *
 * Die bewerkingskolommen zijn de kern van het blad: net als in de Excel staat de hele
 * opdracht op één regel. Onder FINEER 1 staat de fineermaat, onder CNCZ ARDIS B/N de
 * zaagmaat, onder OPDIK uit welk nummer opgedikt wordt. Een lege cel betekent: deze
 * bewerking raakt dit onderdeel niet. De kolomkleuren zijn die van de Excel-koppen en
 * horen bij de bewerking, niet bij de afdeling.
 *
 * De kolommen worden niet vast opgesomd maar uit de regels gehaald: de vereniging van
 * alle stappen die op deze kar voorkomen, in routevolgorde. Een kar zonder fineerwerk
 * heeft dus geen fineerkolommen, en een nieuwe bewerkingsregel in het beheerscherm
 * levert automatisch een nieuwe kolom op.
 *
 * De kolombreedtes komen uit de designspecificatie. Elk gegeven staat één keer —
 * afmetingen en afdeling stonden in de vorige versie meerdere keren per kaart, en dat
 * was een expliciet punt van de stakeholders.
 *
 * Rood en blauw komen van de server: `rodeVelden` zijn de velden met een openstaande
 * rode waarde, `karweiVelden` de maten met overmaat voor karwei. Zodra een afwijking
 * is goedgekeurd verdwijnt het veld uit `rodeVelden` en is de cel dus niet meer rood —
 * de melding blijft wel in de foutcontrole staan.
 */
import { Fragment } from 'react';

import type { Keuzes, Materiaalregel } from '../api';
import { mm } from '../gegevens';
import { Cel, Keuzecel } from './Cel';
import { IconKubus } from './Iconen';
import { tekstOp } from './Routepaneel';

/** Eén bewerkingskolom: de kop, de kleur en de sleutel om de cel per regel te vinden. */
interface Bewerkingskolom {
  sleutel: string;
  kolomnaam: string;
  kleur: string;
  tekstkleur: string;
}

/**
 * De bewerkingskolommen van deze kar: elke stap die bij minstens één onderdeel
 * voorkomt, in de volgorde waarin de regels hem opleveren.
 */
function kolommenVan(regels: Materiaalregel[]): Bewerkingskolom[] {
  const kolommen = new Map<string, Bewerkingskolom>();
  for (const regel of regels) {
    for (const stap of regel.tomtom) {
      if (kolommen.has(stap.sleutel)) continue;
      kolommen.set(stap.sleutel, {
        sleutel: stap.sleutel,
        kolomnaam: stap.kolomnaam,
        kleur: stap.kleur,
        tekstkleur: stap.tekstkleur ?? tekstOp(stap.kleur),
      });
    }
  }
  return [...kolommen.values()];
}

interface Props {
  regels: Materiaalregel[];
  /**
   * De bouwelementen op deze kar. De staat wordt er per stuk op gegroepeerd met een
   * tussenkop, want op één kar staan meerdere samenstellen en de opdracht moet per
   * samenstel te lezen zijn.
   */
  bouwelementen?: Array<{ id: string; code: string; naam: string; onderdelen: number }>;
  keuzes: Keuzes | null;
  magAanpassen: boolean;
  vergrendeld: boolean;
  vergrendelUitleg: string;
  onWijzigen: (regelId: string, wijziging: Record<string, unknown>) => void;
  /**
   * Het 3D-model van één onderdeel opvragen. Zolang de STL-viewer er niet is, laat de
   * kolom zich weg door deze prop niet mee te geven — liever geen kolom dan een knop
   * die niets doet.
   */
  onModel?: (regelId: string) => void;
}

export function Materiaalstaat({
  regels,
  bouwelementen = [],
  keuzes,
  magAanpassen,
  vergrendeld,
  vergrendelUitleg,
  onWijzigen,
  onModel,
}: Props) {
  const gemeen = { magAanpassen, vergrendeld, vergrendelUitleg };
  const kolommen = kolommenVan(regels);

  /*
   * De regels op een rij per bouwelement, in de volgorde van de bouwelementen op de
   * kar. Regels waarvan het bouwelement onbekend is — oude data — komen achteraan
   * onder één kopje, zodat ze niet stil verdwijnen.
   */
  const groepen = (() => {
    const perElement = bouwelementen.map((element) => ({
      element,
      regels: regels.filter((r) => r.bouwelementId === element.id),
    }));
    const bekend = new Set(bouwelementen.map((e) => e.id));
    const rest = regels.filter((r) => !bekend.has(r.bouwelementId));
    return [
      ...perElement.filter((g) => g.regels.length > 0),
      ...(rest.length > 0 ? [{ element: null, regels: rest }] : []),
    ];
  })();

  return (
    <table className="hs-tabel hs-materiaalstaat">
      <colgroup>
        <col className="nr" />
        <col className="aant" />
        <col className="benaming" />
        <col className="maat-l" />
        <col className="maat-b" />
        <col className="maat-d" />
        <col className="materiaal" />
        <col className="zijde" />
        <col className="zijde" />
        <col className="kant" />
        <col className="kant" />
        <col className="kant" />
        <col className="kant" />
        <col className="dek" />
        <col className="opmerkingen" />
        {kolommen.map((k) => (
          <col key={k.sleutel} className="bewerking" />
        ))}
        {onModel ? <col className="model" /> : null}
      </colgroup>
      <thead>
        <tr>
          <th>Nr</th>
          <th>Aant</th>
          <th>Benaming</th>
          <th className="hs-rechts">L</th>
          <th className="hs-rechts">B</th>
          <th className="hs-rechts">D</th>
          <th>Materiaal</th>
          <th>Zijde 0</th>
          <th>Zijde 5</th>
          <th>1_1</th>
          <th>1_2</th>
          <th>2_3</th>
          <th>2_4</th>
          <th>Dek</th>
          <th>Opmerkingen</th>
          {kolommen.map((k) => (
            <th
              key={k.sleutel}
              className="hs-bewerkingkop"
              style={{ background: k.kleur, color: k.tekstkleur }}
              title={k.kolomnaam}
            >
              {k.kolomnaam}
            </th>
          ))}
          {onModel ? <th>3D</th> : null}
        </tr>
      </thead>
      <tbody>
        {groepen.map(({ element, regels: groepregels }) => (
          <Fragment key={element?.id ?? 'zonder'}>
            {/* Tussenkop per bouwelement; bij één bouwelement op de kar is die
                overbodig en blijft hij weg. */}
            {groepen.length > 1 && element ? (
              <tr className="hs-elementkop">
                <td colSpan={15 + kolommen.length + (onModel ? 1 : 0)}>
                  <span className="hs-mono">{element.code}</span> {element.naam}
                  <span className="hs-muted-licht"> · {groepregels.length} onderdelen</span>
                  <span className="hs-mono hs-muted-licht" style={{ marginLeft: 8 }}>
                    {element.id}
                  </span>
                </td>
              </tr>
            ) : null}
            {groepregels.map((regel) => {
          const o = regel.onderdeel;
          const heeftFout = regel.rodeVelden.length > 0;
          const fout = (veld: string) => regel.rodeVelden.includes(veld);
          const wijzig = (wijziging: Record<string, unknown>) => onWijzigen(regel.id, wijziging);

          return (
            <tr key={regel.id} className={heeftFout ? 'hs-rij--fout' : undefined}>
              <td className="hs-mono" style={{ fontWeight: 600 }}>
                {o.nummer}
              </td>
              <td className="hs-mono hs-rechts">
                <Cel
                  {...gemeen}
                  waarde={o.aantal}
                  soort="getal"
                  rechts
                  fout={fout('aantal')}
                  onOpslaan={(v) => wijzig({ aantal: Number(v) })}
                />
              </td>
              <td title={o.benaming}>
                <Cel {...gemeen} waarde={o.benaming} onOpslaan={(v) => wijzig({ benaming: v })} />
              </td>

              {/* Maatvoering. Zit er overmaat voor karwei in, dan wordt de maat blauw. */}
              <td className="hs-mono hs-rechts">
                <Cel
                  {...gemeen}
                  waarde={o.lengte}
                  weergave={mm(o.lengte)}
                  soort="getal"
                  rechts
                  karwei={regel.karweiVelden.includes('lengte')}
                  fout={fout('lengte') || fout('maten')}
                  titel={maatTitel(regel, 'lengte')}
                  onOpslaan={(v) => wijzig({ lengte: Number(v) })}
                />
              </td>
              <td className="hs-mono hs-rechts">
                <Cel
                  {...gemeen}
                  waarde={o.breedte}
                  weergave={mm(o.breedte)}
                  soort="getal"
                  rechts
                  karwei={regel.karweiVelden.includes('breedte')}
                  fout={fout('breedte') || fout('maten')}
                  titel={maatTitel(regel, 'breedte')}
                  onOpslaan={(v) => wijzig({ breedte: Number(v) })}
                />
              </td>
              <td className="hs-mono hs-rechts">
                <Cel
                  {...gemeen}
                  waarde={o.dikte}
                  weergave={mm(o.dikte)}
                  soort="getal"
                  rechts
                  fout={fout('dikte')}
                  titel={diktesTitel(keuzes, o.materiaal)}
                  onOpslaan={(v) => wijzig({ dikte: Number(v) })}
                />
              </td>

              <td>
                <Keuzecel
                  {...gemeen}
                  waarde={o.materiaal}
                  opties={keuzes?.materialen ?? []}
                  titel="Materiaal"
                  metLeeg={false}
                  fout={fout('materiaal')}
                  onOpslaan={(v) => wijzig({ materiaal: v })}
                />
              </td>
              {(['zijde0', 'zijde5'] as const).map((zijde) => (
                <td key={zijde}>
                  <Keuzecel
                    {...gemeen}
                    waarde={o.zijden[zijde]}
                    opties={keuzes?.afwerkingen ?? []}
                    titel={`Afwerking ${zijde === 'zijde0' ? 'zijde 0' : 'zijde 5'}`}
                    fout={fout(zijde)}
                    onOpslaan={(v) => wijzig({ zijden: { [zijde]: v } })}
                  />
                </td>
              ))}
              {(['zijde1', 'zijde2', 'zijde3', 'zijde4'] as const).map((zijde) => (
                <td key={zijde}>
                  <Keuzecel
                    {...gemeen}
                    waarde={o.zijden[zijde]}
                    opties={keuzes?.kanten ?? []}
                    titel={`Kantenband ${zijde.replace('zijde', 'zijde ')}`}
                    fout={fout(zijde)}
                    onOpslaan={(v) => wijzig({ zijden: { [zijde]: v } })}
                  />
                </td>
              ))}

              <td className="hs-mono">{o.dekkend ? 'Y' : ''}</td>
              <td title={o.opmerking ?? ''}>
                <Cel
                  {...gemeen}
                  waarde={o.opmerking ?? ''}
                  weergave={opmerkingTekst(regel)}
                  onOpslaan={(v) => wijzig({ opmerking: v })}
                />
              </td>

              {/* Per bewerking wat er met dít onderdeel moet gebeuren. Leeg = deze
                  bewerking raakt dit onderdeel niet; dan blijft de cel ook kleurloos,
                  zodat het oog alleen langs het werk gaat dat er ligt. */}
              {kolommen.map((kolom) => {
                const stap = regel.tomtom.find((t) => t.sleutel === kolom.sleutel);
                if (!stap) return <td key={kolom.sleutel} className="hs-bewerkingcel" />;
                return (
                  <td
                    key={kolom.sleutel}
                    className="hs-bewerkingcel hs-bewerkingcel--vol"
                    style={{ background: kolom.kleur, color: kolom.tekstkleur }}
                    title={[kolom.kolomnaam, stap.label, stap.toelichting]
                      .filter(Boolean)
                      .join('\n')}
                  >
                    {stap.label || '·'}
                  </td>
                );
              })}
              {onModel ? (
                <td>
                  <button
                    type="button"
                    className="hs-knop hs-knop--mini"
                    title="3D-model van dit onderdeel"
                    onClick={() => onModel(regel.id)}
                  >
                    3D
                  </button>
                </td>
              ) : null}
            </tr>
          );
            })}
          </Fragment>
        ))}
      </tbody>
    </table>
  );
}

/** De onderbouwing van een maat: netto, bruto en waar de toeslagen vandaan komen. */
function maatTitel(regel: Materiaalregel, richting: 'lengte' | 'breedte'): string {
  const netto = regel.maten.netto[richting];
  const bruto = regel.maten.bruto[richting];
  const karwei = regel.maten.karwei[richting];
  const delen = [`netto ${mm(netto)} mm → zaagmaat ${mm(bruto)} mm`];
  if (karwei) delen.push(`waarvan ${mm(karwei)} mm overmaat karwei`);
  for (const toeslag of regel.maten.toeslagen) delen.push(toeslag.toelichting);
  return delen.join('\n');
}

function diktesTitel(keuzes: Keuzes | null, materiaal: string): string | undefined {
  const diktes = keuzes?.diktes[materiaal.toUpperCase()];
  if (!diktes || diktes.length === 0) return undefined;
  return `Geleverd in ${materiaal}: ${diktes.map((d) => mm(d)).join(', ')} mm`;
}

/** De opmerking, met de overmaat karwei erbij als die er is. */
function opmerkingTekst(regel: Materiaalregel): string {
  const delen: string[] = [];
  const karwei = regel.maten.karwei;
  if (karwei.lengte || karwei.breedte) {
    const maten = [
      karwei.lengte ? `${karwei.lengte > 0 ? '+' : ''}${mm(karwei.lengte)} mm lengte` : null,
      karwei.breedte ? `${karwei.breedte > 0 ? '+' : ''}${mm(karwei.breedte)} mm breedte` : null,
    ].filter(Boolean);
    delen.push(`${maten.join(', ')} overmaat karwei`);
  }
  if (regel.onderdeel.opmerking) delen.push(regel.onderdeel.opmerking);
  return delen.join(' · ') || '—';
}

/**
 * De legenda onder de materiaalstaat: de bewerkingen, de afdelingen en rework.
 *
 * De bewerkingen staan vooraan: dat zijn de kleuren van de kolommen in de staat zelf.
 * De afdelingskleuren staan erachter, want die gaan over de werkvloer — waar de kar
 * staat, niet wat er met het onderdeel moet gebeuren.
 */
export function Legenda({
  afdelingen,
  rework,
  bewerkingen,
}: {
  afdelingen: Array<{ afdeling: string; kleur: string }>;
  rework: { kleur: string; rand: string };
  bewerkingen: Array<{ bewerking: string; kleur: string }>;
}) {
  return (
    <div className="hs-legenda">
      <span className="hs-sectielabel">Bewerkingen</span>
      {bewerkingen.map((b) => (
        <span key={b.bewerking} className="item">
          <span
            className="hs-kleurvak"
            style={{ background: b.kleur, boxShadow: 'inset 0 0 0 1px rgba(0,0,0,.18)' }}
          />
          {b.bewerking}
        </span>
      ))}
      <span className="hs-sectielabel" style={{ marginLeft: 8 }}>
        Afdelingen
      </span>
      {afdelingen.map((a) => (
        <span key={a.afdeling} className="item">
          <span className="hs-kleurvak" style={{ background: a.kleur }} />
          {a.afdeling}
        </span>
      ))}
      <span className="item">
        <span
          className="hs-kleurvak"
          style={{ background: rework.kleur, boxShadow: `inset 0 0 0 1px ${rework.rand}` }}
        />
        Rework
      </span>
      <span className="item hs-muted-licht" style={{ marginLeft: 'auto' }}>
        <IconKubus maat={12} /> per onderdeel op te vragen
      </span>
    </div>
  );
}

export { tekstOp };
